import { createMiddleware, createStart } from '@tanstack/react-start'
import { enforceScraperGate } from './server/scraper-gate'

// Refuses the undeclared scraper before anything is rendered. First in the
// chain because the whole point is to spend nothing on it: a match here costs
// a header read, where letting it through costs an SSR pass and a dictionary
// RPC call. See server/scraper-gate.ts for the measurements and the rule.
const scraperGate = createMiddleware({ type: 'request' }).server(async ({ next, request }) => {
   // Returning a Response short-circuits the chain: nothing downstream runs,
   // so a refusal costs neither the SSR pass nor the dictionary RPC call.
   const refusal = enforceScraperGate(request)
   if (refusal) return refusal
   return await next()
})

// Server-rendered HTML names the current build's hashed assets, so a cached
// copy outlives the build it describes: replay it after a deploy and every
// chunk it points at is already gone. `no-cache` does not stop the browser
// storing the document, it stops it *reusing* one without revalidating, which
// is what lets a reload recover from version skew instead of re-serving the
// same broken page. Pairs with installStaleChunkReload() in src/router.tsx.
//
// This runs as global request middleware rather than in a custom server entry
// (src/server.ts, Start's own convention for this). That entry is dead code
// here: the Cloudflare plugin feeds wrangler.jsonc's `main` in as the ssr
// build input, and Start only falls back to resolving src/server.ts when that
// input is absent — so the file is silently never bundled. Middleware is
// reached through the start-entry alias, which the worker entry cannot
// override.
//
// Assets are exempt by construction: Workers Assets serves them without
// entering the worker, and their content hashes make them immutable anyway.
const htmlCachePolicy = createMiddleware({ type: 'request' }).server(async ({ next }) => {
   const result = await next()
   const { response } = result

   // Server functions answer with JSON and set their own policy; only the
   // rendered document is at issue here.
   const contentType = response.headers.get('content-type')
   if (contentType === null || !contentType.includes('text/html')) return result
   if (response.headers.has('cache-control')) return result

   const headers = new Headers(response.headers)
   headers.set('Cache-Control', 'no-cache')

   // Re-wrapping passes the body through as a stream, so the shell still
   // reaches the browser before the render finishes.
   return new Response(response.body, {
      status: response.status,
      statusText: response.statusText,
      headers,
   })
})

export const startInstance = createStart(() => ({
   requestMiddleware: [scraperGate, htmlCachePolicy],
}))
